import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../services/supabaseClient';
import { subscribeWithAuthRecovery } from '../services/realtimeResilience';
import { fetchMaintenance, releaseMaintenanceForBuild } from '../services/maintenanceService';
import {
    clockOffsetMs,
    describeMaintenance,
    istGleicheMaintenanceZeile,
    istGleicherMaintenanceStand,
    MaintenanceRow,
    MaintenanceState,
    shouldReleaseForBuild,
    sollMindestBuildMelden,
} from '../services/maintenanceLogic';
import { markiereBuildVeraltet } from '../services/versionGate';

/**
 * Die Bauphase aus Sicht EINES Clients: holen, frisch halten, Phase rechnen.
 *
 * Der Hook liefert Daten und Zustand, sonst nichts. Was daraus auf dem Schirm
 * steht — Countdown-Leiste, Wartungsseite, Admin-Knopf — entscheidet die
 * Oberflaeche; die Regeln dahinter stehen in maintenanceLogic.ts und sind dort
 * ohne DOM testbar, hier nicht (kein jsdom).
 *
 * Drei Wege halten den Stand aktuell, und keiner ist allein verlaesslich:
 *   * Realtime auf `app_maintenance` — sofort, aber nach Standby oder
 *     Netzabriss gern tot.
 *   * Abgleich alle 30 s, beim Sichtbarwerden des Tabs und bei `online` —
 *     der Ausfallschutz fuer Realtime.
 *   * ein Zeitgeber, der sich nach `state.nextCheckMs` selbst neu plant —
 *     denn die Phase wechselt auch dann, wenn sich an den DATEN nichts tut
 *     (Muster describeIdleCountdown in App.tsx: kein 1-s-Dauerintervall).
 *
 * FAIL-OPEN: `fetchMaintenance` wirft nie; ohne Antwort bleibt es bei 'none'.
 * Der eigentliche Riegel sitzt serverseitig im Trigger.
 *
 * ─── „Das Update ist da" (Nr. 95/96) ──────────────────────────────────────
 * Wer `buildTime` hereinreicht (App.tsx: `__BUILD_TIME__`), meldet sich nach
 * jedem Abgleich als Update, sobald sein Build NEUER ist als der beim
 * Ankuendigen ausgelieferte (`row.deployedBuild`): ein Aufruf von
 * `maintenance_release`, hoechstens einer je Ankuendigung. Der Server
 * beendet die Bauphase, Realtime traegt das zur Flotte. So endet die Bauphase
 * von selbst, sobald der erste Client mit dem neuen Build antritt — der
 * Administrator muss nichts mehr klicken.
 *
 * ─── „Dieses Programm ist zu alt" (Nr. 112/113) ──────────────────────────
 * Dieselbe Zeile fuehrt seit dem Build-Riegel auch `min_build`. Wer
 * `buildTime` UND `onBuildTooOld` hereinreicht, bekommt gemeldet, sobald
 * der eigene Build darunter liegt — genau dann, wenn der Server jeden
 * Schreibvorgang dieses Tabs ohnehin mit `BUILD_VERALTET` abweisen wuerde.
 * Der Befund wird sofort vermerkt (`markiereBuildVeraltet`), damit kein
 * Schreibweg es erst ausprobiert; was auf dem Bildschirm daraus wird,
 * entscheidet der Aufrufer (App.tsx: Reload oder Update-Balken).
 *
 * Einmal je Mindest-Build: die Meldung soll nicht bei jedem Abgleich neu
 * anklopfen. Ein STRENGERER Riegel meldet erneut — sonst verschluckt der
 * Merker die zweite Stufe.
 */

/** Ausfallschutz fuer Realtime — dieselbe Spanne wie der Rechenzeitgeber in 'announced'. */
const RECONCILE_MS = 30_000;

/** Kanalnamen muessen eindeutig sein — zwei gleichnamige Abos stolpern beim Abbau uebereinander. */
let channelSeq = 0;

const NONE: MaintenanceState = describeMaintenance({ row: null, nowMs: 0 });

export interface MaintenanceView {
    state: MaintenanceState;
    row: MaintenanceRow | null;
    /** Sofort neu abgleichen — z. B. nach einem Admin-Klick oder einem BAUPHASE-Fehler. */
    refresh: () => Promise<void>;
}

/**
 * @param enabled Aus ohne Sitzung (Login-Seite) — dort laeuft weder Abo noch Zeitgeber.
 * @param role Rolle des Betrachters; nur fuer `adminBypass`.
 * @param buildTime Build-Zeitstempel dieses Bundles (`__BUILD_TIME__`); nur
 *   wer ihn hereinreicht, meldet „Update ist da" und prueft sich gegen den
 *   Mindest-Build. Die Admin-Karte laesst ihn weg — dort meldet schon das
 *   Gate in App.tsx, und ein Reload mitten im Ankuendigen waere das Letzte,
 *   was der Administrator gebrauchen kann.
 * @param onBuildTooOld Wird einmal je Mindest-Build gerufen, sobald dieses
 *   Bundle darunter liegt. Ohne `buildTime` nie.
 */
export const useMaintenance = (args: {
    enabled: boolean;
    role?: string | null;
    buildTime?: string | null;
    onBuildTooOld?: () => void;
}): MaintenanceView => {
    const { enabled, role, buildTime, onBuildTooOld } = args;

    const [row, setRow] = useState<MaintenanceRow | null>(null);
    const [state, setState] = useState<MaintenanceState>(NONE);

    // Server minus Client in ms. Im Ref, weil der Zeitgeber ihn zwischen zwei
    // Abgleichen braucht, ohne dass ein Setzen einen neuen Effektlauf ausloest.
    const offsetRef = useRef(0);
    const rowRef = useRef<MaintenanceRow | null>(null);
    const roleRef = useRef(role);
    roleRef.current = role;
    const buildTimeRef = useRef(buildTime ?? null);
    buildTimeRef.current = buildTime ?? null;
    /**
     * Fuer welche Ankuendigung (`announcedAt`) die Deploy-Meldung schon raus
     * ist. Ein Aufruf je Ankuendigung reicht: trifft er, endet die Bauphase;
     * trifft er nicht (Server sagt „nicht neuer"), aendert Wiederholen nichts.
     */
    const releasedForRef = useRef<string | null>(null);
    /** Fuer welchen Mindest-Build schon gemeldet wurde — einmal je Wert. */
    const buildGemeldetFuerRef = useRef<string | null>(null);
    const onBuildTooOldRef = useRef(onBuildTooOld);
    onBuildTooOldRef.current = onBuildTooOld;

    // Neu rechnen — mit der um den Server-Versatz korrigierten Uhr.
    const recompute = useCallback((): MaintenanceState => {
        const next = describeMaintenance({
            row: rowRef.current,
            nowMs: Date.now() + offsetRef.current,
            role: roleRef.current,
        });
        // Gleichwertigen Stand NICHT ersetzen: mit gesetztem Mindest-Build
        // liefert describeMaintenance auch in der Ruhelage jedes Mal ein
        // frisches Objekt, und der Zeitgeber rechnet alle 30 s nach — ohne
        // diesen Vergleich renderte die ganze App im 30-s-Takt neu.
        setState(prev => (istGleicherMaintenanceStand(prev, next) ? prev : next));
        return next;
    }, []);

    // Laeuft der Hook gerade? Ein Abgleich, der nach dem Abbau zurueckkommt,
    // darf keinen Zustand mehr setzen.
    const aliveRef = useRef(false);

    const refresh = useCallback(async (): Promise<void> => {
        if (!aliveRef.current) return;
        const clientNow = Date.now();
        const result = await fetchMaintenance();
        if (!aliveRef.current) return;
        if (result.serverNowMs !== null) {
            offsetRef.current = clockOffsetMs(new Date(result.serverNowMs).toISOString(), clientNow);
        }
        // Lesefehler ≠ Wartungsende (Codex-Review 09.09.2026). `fetchMaintenance`
        // liefert bei JEDEM Fehler `{ row: null, serverNowMs: null }` — offline,
        // abgelaufenes Token, Serverpause waehrend des Deploys. Wuerde das hier
        // die Zeile leeren, hielte das Gate eine laufende Sperre fuer beendet
        // und lueden die Clients mitten im Netzverlust neu. Ein bestaetigtes
        // Ende kommt IMMER mit `serverNowMs` und einer Zeile (`active=false`);
        // nur dann wird uebernommen. Ohne Zeile bleibt der letzte bekannte
        // Stand stehen — die lokale Uhr laesst ihn ueber `expiresAt` ohnehin
        // von selbst auslaufen, wenn der Server dauerhaft schweigt.
        const lesefehler = result.row === null && result.serverNowMs === null;
        if (!lesefehler) {
            // Der Ref traegt IMMER den frischen Stand — an ihm haengen die
            // Regeln. In den React-Zustand geht er nur, wenn sich wirklich
            // ein Feld geaendert hat: `fetchMaintenance` baut bei jedem
            // Abgleich ein neues Objekt, und der laeuft alle 30 Sekunden.
            const vorher = rowRef.current;
            rowRef.current = result.row;
            if (!istGleicheMaintenanceZeile(vorher, result.row)) setRow(result.row);
        }
        recompute();

        // „Dieses Programm ist zu alt": der Server fuehrt einen Mindest-Build,
        // unter dem dieses Bundle liegt. Der Riegel gilt unabhaengig von
        // `active` — er ist Normalbetrieb, keine Bauphase.
        const eigener = buildTimeRef.current;
        const mindest = rowRef.current?.minBuild ?? null;
        if (sollMindestBuildMelden(eigener, mindest, buildGemeldetFuerRef.current)) {
            buildGemeldetFuerRef.current = mindest;
            markiereBuildVeraltet();
            onBuildTooOldRef.current?.();
        }

        // „Das Update ist da": dieser Build ist neuer als der angekuendigte.
        const own = buildTimeRef.current;
        const current = rowRef.current;
        if (own && current && shouldReleaseForBuild(current, own)) {
            const key = current.announcedAt ?? 'ohne-datum';
            if (releasedForRef.current !== key) {
                releasedForRef.current = key;
                const released = await releaseMaintenanceForBuild(own);
                if (!aliveRef.current) return;
                if (released) {
                    rowRef.current = released;
                    setRow(released);
                    recompute();
                }
            }
        }
    }, [recompute]);

    useEffect(() => {
        if (!enabled) {
            aliveRef.current = false;
            rowRef.current = null;
            setRow(null);
            setState(NONE);
            return;
        }

        aliveRef.current = true;
        let timer: ReturnType<typeof setTimeout> | null = null;

        // Der sich selbst neu planende Zeitgeber: nach jedem Rechenlauf den
        // naechsten Blick genau dort ansetzen, wo sich etwas aendern kann.
        const tick = () => {
            if (!aliveRef.current) return;
            const next = recompute();
            timer = setTimeout(tick, next.nextCheckMs);
        };
        const reschedule = () => {
            if (timer) clearTimeout(timer);
            tick();
        };

        // Erste Ladung, danach den Zeitgeber auf den frischen Stand setzen.
        void refresh().then(reschedule);

        // Realtime: jede Aenderung an der einen Zeile zieht den Stand nach.
        // Der Inhalt des Payloads wird bewusst nicht gelesen — die RPC rechnet
        // `active` und `server_now` serverseitig, das Abo ist nur der Anstoss.
        const handle = subscribeWithAuthRecovery(supabase, () =>
            supabase
                .channel(`app-maintenance-${++channelSeq}`)
                .on(
                    'postgres_changes',
                    { event: '*', schema: 'public', table: 'app_maintenance' },
                    () => { void refresh().then(reschedule); },
                ),
        );

        // Ausfallschutz: Realtime kann fehlen oder nach Standby tot sein.
        const reconcile = setInterval(() => {
            if (typeof document !== 'undefined' && document.hidden) return;
            void refresh().then(reschedule);
        }, RECONCILE_MS);

        const onVisible = () => {
            if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
            void refresh().then(reschedule);
        };
        const onOnline = () => { void refresh().then(reschedule); };
        if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisible);
        if (typeof window !== 'undefined') window.addEventListener('online', onOnline);

        return () => {
            aliveRef.current = false;
            if (timer) clearTimeout(timer);
            clearInterval(reconcile);
            handle.close();
            if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisible);
            if (typeof window !== 'undefined') window.removeEventListener('online', onOnline);
        };
    }, [enabled, refresh, recompute]);

    // Rollenwechsel (Profil nachgeladen) aendert nur `adminBypass` — ohne
    // Neuaufbau von Abo und Zeitgeber.
    useEffect(() => {
        if (enabled && aliveRef.current) recompute();
    }, [enabled, role, recompute]);

    return { state, row, refresh };
};
